/** @flow */

import { takeEvery, put, all, call, spawn, select } from "redux-saga/effects";
import http from "@http";
import config from "@config";
import { toast } from "react-toastify";
import { getFormValues } from "redux-form";
import moment from "moment";

import {
  GetDataFiles,
  GetValidatingStatus,
  CreateDataFile,
  GetActiveHierarchy,
  GetExportHistory,
  ExportFileLogHistory,
  CancelProcessing,
  CancelProcessingSuccess,
  CancelProcessingError,
  GetTransferHistory,
  PostDataTransferFile,
  ExportTransferFileHistory,
  GetDataExceptionTypes,
  GetDataExceptionsByType,
  SetDataExceptionTypeConfirm,
  DeleteDataFile,
  ApproveDataExceptions,
  GetDataDashboardOverview,
  GetDataQualityIssues,
  ExportDataQualityIssues,
  ExportDataFile,
  DownloadTransferHistoryFile,
  ExportSipFileExceptions,
  DeleteTransferHistoryFile,
  ReviewDataChanges,
  SetDataFileStatus,
  GetReviewDataChanges,
} from "@modules/data/constants/actions";
import { dataURLtoFile } from "@utils/file";

import actionCreators from "@modules/data/actionCreator";
import type { DataFile } from "@modules/data/types";
import type { Action } from "redux-action";
import type { PaginationType } from "@types";
import { transformFilter } from "@transformers/request";
import { parseSchema } from "@utils/schema";
import { toFormData } from "@utils/form-data";
import dataFileArchive from "./data-file-archive";
import {
  DATA_FILE_TYPES,
  DATA_FILE_STATUSES,
  GET_DATA_FILES_FILTERS,
} from "@modules/data/constants/enums";
import { downloadCsvFile, downloadXslsFile } from "@sagas/utils";
import { getTransferFileHistoryFilters } from "@modules/data/selectors";

export type DataFilesListRequest = $Exact<{
  clientId: number,
  filters: Object,
}>;

export type CreateDataFileRequest = $Exact<{
  clientId: number,
  formData: Object,
}>;

export type GetActiveHierarchyRequest = $Exact<{
  clientId: number,
  includeEmployeePositions: boolean,
  formData?: Object,
  file?: string,
  page?: number,
}>;

export type DeleteFileHistoryRequest = $Exact<{
  clientId: number,
  fileId: string,
}>;

export type ListResponse<T> = $Exact<{
  content: T,
  pagination: PaginationType,
}>;

export type GetExceptionTypes = $Exact<{
  clientId: number,
  fileId: number,
  fileType: string,
  page: number,
}>;

export type GetExceptionsByType = $Exact<{
  clientId: number,
  fileId: number,
  typeId: number,
  fileType: string,
  page: number,
}>;

export type ConfirmExceptionType = $Exact<{
  clientId: number,
  fileType: string,
  fileId: number,
  exceptionTypeId: number,
  exceptionTypeData: Object,
}>;

export type UpdateDataFile = $Exact<{
  clientId: number,
  file: Object,
}>;

export type ExportDataFileRequest = $Exact<{
  clientId: number,
  departmentId: number,
}>;

export type DownloadTransferHistoryFileRequest = $Exact<{
  clientId: number,
  documentId: number,
  name: string,
}>;

const getExceptionsEndpoint = ({ type, client, fileId, typeId }: Object) => {
  const endpoints = {
    [DATA_FILE_TYPES.HIERARCHY_LOAD]: `/api/v1/data-admin/client/${client}/hierarchy/files/${fileId}/exception-types/${typeId}/exceptions`,
    [DATA_FILE_TYPES.HISTORIC_ABSENCE]: `/api/v1/data-admin/client/${client}/historic/files/${fileId}/exception-types/${typeId}/exceptions`,
    [DATA_FILE_TYPES.MY_FIRSTCARE_ACCOUNTS]: `/api/v1/data-admin/client/${client}/myfirstcare/files/${fileId}/exception-types/${typeId}/exceptions`,
    [DATA_FILE_TYPES.STAFF_IN_POST]: `/api/v1/data-admin/client/${client}/sip/files/${fileId}/exception-types/${typeId}/exceptions`,
  };

  if (!endpoints[type]) {
    throw Error("Unsupported data file type");
  }

  return endpoints[type];
};

const getExceptionsTypeEndpoint = ({ type, client, fileId }: Object) => {
  const endpoints = {
    [DATA_FILE_TYPES.HIERARCHY_LOAD]: `/api/v1/data-admin/client/${client}/hierarchy/files/${fileId}/exception-types`,
    [DATA_FILE_TYPES.HISTORIC_ABSENCE]: `/api/v1/data-admin/client/${client}/historic/files/${fileId}/exception-types`,
    [DATA_FILE_TYPES.MY_FIRSTCARE_ACCOUNTS]: `/api/v1/data-admin/client/${client}/myfirstcare/files/${fileId}/exception-types`,
    [DATA_FILE_TYPES.STAFF_IN_POST]: `/api/v1/data-admin/client/${client}/sip/files/${fileId}/exception-types`,
  };

  if (!endpoints[type]) {
    throw Error("Unsupported data file type");
  }

  return endpoints[type];
};

const getUpdateTypeEndpoint = ({ type, client, fileId, typeId }: Object) => {
  const endpoints = {
    [DATA_FILE_TYPES.HIERARCHY_LOAD]: `/api/v1/data-admin/client/${client}/hierarchy/files/${fileId}/exception-types/${typeId}`,
    [DATA_FILE_TYPES.HISTORIC_ABSENCE]: `/api/v1/data-admin/client/${client}/historic/files/${fileId}/exception-types/${typeId}`,
    [DATA_FILE_TYPES.MY_FIRSTCARE_ACCOUNTS]: `/api/v1/data-admin/client/${client}/myfirstcare/files/${fileId}/exception-types/${typeId}`,
    [DATA_FILE_TYPES.STAFF_IN_POST]: `/api/v1/data-admin/client/${client}/sip/files/${fileId}/exception-types/${typeId}`,
  };

  if (!endpoints[type]) {
    throw Error("Unsupported data file type");
  }

  return endpoints[type];
};

const getUpdateFileEndpoint = ({ type, client, fileId }: Object) => {
  const endpoints = {
    [DATA_FILE_TYPES.HIERARCHY_LOAD]: `/api/v1/data-admin/client/${client}/hierarchy/files/${fileId}`,
    [DATA_FILE_TYPES.HISTORIC_ABSENCE]: `/api/v1/data-admin/client/${client}/historic/files/${fileId}`,
    [DATA_FILE_TYPES.MY_FIRSTCARE_ACCOUNTS]: `/api/v1/data-admin/client/${client}/myfirstcare/files/${fileId}`,
    [DATA_FILE_TYPES.STAFF_IN_POST]: `/api/v1/data-admin/client/${client}/sip/files/${fileId}`,
  };

  if (!endpoints[type]) {
    throw Error("Unsupported data file type");
  }

  return endpoints[type];
};

function* onGetDataFiles({ payload }: Action<DataFilesListRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }
    const { clientId, filters } = payload;

    const response = yield call(http.get, `/api/v1/data-admin/client/${clientId}/files/details`, {
      params: {
        search: transformFilter(filters),
      },
    });

    yield put(actionCreators.GetDataFilesSuccess(response.data.content[0]));
  } catch (e) {
    yield put(actionCreators.GetDataFilesError(e.response));
  }
}

function* onGetValidatingStatus({
  payload,
}: Action<DataFilesListRequest>): Generator<*, void, { data: DataFile }> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/data-admin/client/${payload.clientId}/files/status`,
    );
    const { data } = response;
    yield put(actionCreators.GetValidatingStatusSuccess(data));

    if (!data.id) {
      yield put(actionCreators.GetDataDashboardOverview({ clientId: payload.clientId }));
    }
  } catch (e) {
    yield put(actionCreators.GetDataFilesError(e.response));
  }
}

function* onCreateDataFile({ payload }: Action<CreateDataFileRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const schema = yield parseSchema("DataMigrationFileUploadFormDTO");
    const form = toFormData(schema, payload.formData);

    const response = yield http.post(
      `api/v1/data-admin/client/${payload.clientId}/sip/files`,
      form,
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    );

    toast.success("Thank you, your file has been transferred successfully");

    yield all([
      put(actionCreators.CreateDataFileSuccess(response.data)),
      put(
        actionCreators.GetDataFiles({
          clientId: payload.clientId,
          filters: GET_DATA_FILES_FILTERS,
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.CreateDataFileError(e.response));
  }
}

function* onGetActiveHierarchy({
  payload,
}: Action<GetActiveHierarchyRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v1/hierarchy-admin/client/${payload.clientId}/hierarchy/active`,
      {
        params: {
          includeEmployeePositions: payload.includeEmployeePositions,
        },
      },
    );

    yield put(actionCreators.GetActiveHierarchySuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetActiveHierarchyError(e.response));
  }
}

function* onGetExportHistory({ payload }: Action<CreateDataFileRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.formData) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }
    const { clientId, formData } = payload;
    const { from_date, to_date } = formData;

    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/data-admin/client/${clientId}/sip/files/file-log`,
      {
        params: { from_date, to_date },
      },
    );

    const { data } = response;
    yield put(actionCreators.GetExportHistorySuccess(data));
  } catch (e) {
    yield put(actionCreators.GetExportHistoryError(e.response));
  }
}
export function* onExportFileLogHistory({
  payload,
}: Action<GetActiveHierarchyRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.formData) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const from_date = (payload.formData || {}).from_date;
    const to_date = (payload.formData || {}).to_date;

    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/data-admin/client/${payload.clientId}/sip/files/file-log/export?from_date=${from_date}&to_date=${to_date}`,
      {
        params: { from_date, to_date },
      },
    );

    downloadCsvFile(
      response.data,
      `File Log History - Client ${payload.clientId} Period ${from_date} - ${to_date}.csv`,
    );
    yield put(actionCreators.ExportFileLogHistorySuccess(response.data));
  } catch (e) {
    yield put(actionCreators.ExportFileLogHistoryError(e.response));
  }
}

export function* onCancelProcessing({
  payload,
}: Action<{ clientId: number, fileId: number }>): Generator<*, void, *> {
  try {
    if (!payload || !payload.fileId || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.put,
      `${config.endpointBaseUrl}api/v1/data-admin/client/${payload.clientId}/sip/files/${payload.fileId}`,
      { status: "CANCELLED" },
    );

    yield put(actionCreators.SetDataFileStatus("NO_FILE"));
  } catch (e) {
    console.log("CancelProcessingError", e);

    yield put(actionCreators.CancelProcessingError(e.response));
  }
}

function* onPostDataTransferFile({
  payload,
}: Action<GetActiveHierarchyRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.file) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const file = dataURLtoFile(payload.file);

    // Preparing "multipart/form-data" request
    const data = new FormData();
    data.append("file", file.blob, decodeURIComponent(file.name));

    const response = yield call(
      http.post,
      `${config.endpointBaseUrl}api/v1/data-admin/client/${payload.clientId}/sip/data-transfer/document`,
      data,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    const from_date = moment()
      .subtract(30, "days")
      .format(config.dateFormatBE);
    const to_date = moment().format(config.dateFormatBE);

    yield all([
      put(actionCreators.PostDataTransferFileSuccess(response.data)),
      put(
        actionCreators.GetTransferHistory({
          clientId: payload.clientId,
          formData: { from_date, to_date },
        }),
      ),
    ]);
    toast.success("Thank you, your file has been transferred successfully");
  } catch (e) {
    yield put(actionCreators.PostDataTransferFileError(e.response));
  }
}

function* onGetTransferHistory({
  payload,
}: Action<GetActiveHierarchyRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.formData) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }
    const filters = yield select(getTransferFileHistoryFilters);
    const page = payload.page || 1;

    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/data-admin/client/${payload.clientId}/sip/data-transfer/document`,
      {
        params: { ...filters, page, sort: "created_at,desc" },
      },
    );

    yield put(actionCreators.GetTransferHistorySuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetTransferHistoryError(e.response));
  }
}

function* onDeleteTransferHistoryFile({
  payload,
}: Action<DeleteFileHistoryRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.fileId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const filters = yield select(getTransferFileHistoryFilters);

    const response = yield call(
      http.delete,
      `${config.endpointBaseUrl}api/v1/data-admin/client/${payload.clientId}/sip/data-transfer/document/${payload.fileId}`,
    );

    yield put(actionCreators.DeleteTransferHistoryFileSuccess());
    yield put(actionCreators.GetTransferHistory({ formData: filters, clientId: payload.clientId }));
  } catch (e) {
    yield put(actionCreators.DeleteTransferHistoryFileError(e.response));
  }
}

export function* onExportTransferFileHistory({
  payload,
}: Action<GetActiveHierarchyRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.formData) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const from_date = (payload.formData || {}).from_date;
    const to_date = (payload.formData || {}).to_date;

    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/data-admin/client/${payload.clientId}/sip/data-transfer/document/export`,
      {
        params: { search: `created_at>${from_date},created_at<=${to_date}` },
      },
    );

    downloadCsvFile(
      response.data,
      `Transfer File History - Client ${payload.clientId} Period ${from_date} - ${to_date}.csv`,
    );
    yield put(actionCreators.ExportTransferFileHistorySuccess(response.data));
  } catch (e) {
    yield put(actionCreators.ExportTransferFileHistoryError(e.response));
  }
}

function* onGetDataExceptionTypes({ payload }: Action<GetExceptionTypes>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.fileType || !payload.fileId || !payload.page) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const endpoint = getExceptionsTypeEndpoint({
      type: payload.fileType,
      client: payload.clientId,
      fileId: payload.fileId,
    });
    const response = yield call(http.get, endpoint);

    // get exceptions for every group
    const exceptionsCall = response.data.map(e => {
      const typeId = e.id;
      return put(
        actionCreators.GetDataExceptionsByType({
          ...payload,
          typeId,
          page: payload.page,
        }),
      );
    });

    yield all([put(actionCreators.GetDataExceptionTypesSuccess(response.data)), ...exceptionsCall]);
  } catch (e) {
    yield put(actionCreators.GetDataExceptionTypesError(e.response));
  }
}

function* onGetDataExceptionsByType({
  payload,
}: Action<GetExceptionsByType>): Generator<*, void, *> {
  try {
    if (
      !payload ||
      !payload.clientId ||
      !payload.fileType ||
      !payload.fileId ||
      !payload.typeId ||
      !payload.page
    ) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const endpoint = getExceptionsEndpoint({
      type: payload.fileType,
      client: payload.clientId,
      fileId: payload.fileId,
      typeId: payload.typeId,
    });
    const response = yield call(http.get, endpoint, {
      params: {
        page: payload.page,
        limit: config.resultsPerPage,
      },
    });

    yield put(
      actionCreators.GetDataExceptionsByTypeSuccess({ ...response.data, typeId: payload.typeId }),
    );
  } catch (e) {
    yield put(actionCreators.GetDataExceptionsByTypeError(e.response));
  }
}

function* onSetDataExceptionTypeConfirm({
  payload,
}: Action<ConfirmExceptionType>): Generator<*, void, *> {
  try {
    if (
      !payload ||
      !payload.clientId ||
      !payload.fileType ||
      !payload.fileId ||
      !payload.exceptionTypeId ||
      !payload.exceptionTypeData
    ) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const endpoint = getUpdateTypeEndpoint({
      type: payload.fileType,
      client: payload.clientId,
      fileId: payload.fileId,
      typeId: payload.exceptionTypeId,
    });

    const response = yield call(http.put, endpoint, payload.exceptionTypeData);

    yield put(actionCreators.SetDataExceptionTypeConfirmSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.SetDataExceptionTypeConfirmError(e.response));
  }
}

function* onDeleteDataFile({ payload }: Action<UpdateDataFile>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.file) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    // update status of file so being deleted
    const file = { ...payload.file, status: DATA_FILE_STATUSES.CANCELLED };
    const endpoint = getUpdateFileEndpoint({
      type: payload.file.type,
      client: payload.clientId,
      fileId: payload.file.id,
    });
    const response = yield call(http.put, endpoint, file);

    yield all([
      put(actionCreators.DeleteDataFileSuccess(response.data)),
      put(
        actionCreators.GetDataFiles({
          clientId: payload.clientId,
          filters: GET_DATA_FILES_FILTERS,
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.DeleteDataFileError(e.response));
  }
}

function* onApproveDataExceptions({ payload }: Action<UpdateDataFile>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.file) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    // update status of file so being processed
    const file = { ...payload.file, status: DATA_FILE_STATUSES.QUEUED };
    const endpoint = getUpdateFileEndpoint({
      type: payload.file.type,
      client: payload.clientId,
      fileId: payload.file.id,
    });
    const response = yield call(http.put, endpoint, file);

    yield put(actionCreators.ApproveDataExceptionsSuccess(response.data));
    yield put(actionCreators.ToggleReviewDataChangesModal());
  } catch (e) {
    yield put(actionCreators.ApproveDataExceptionsError(e.response));
  }
}

function* onGetDataDashboardOverview({
  payload,
}: Action<{ clientId: number }>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/data-admin/client/${payload.clientId}/data-quality`,
    );

    yield put(actionCreators.GetDataDashboardOverviewSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetDataDashboardOverviewError(e.response));
  }
}

function* onExportDataQualityIssues({
  payload,
}: Action<{ clientId: number }>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }
    const response = yield call(
      http.get,
      `api/v1/data-admin/client/${payload.clientId}/data-quality/issues/export`,
    );
    downloadCsvFile(response.data, `Data_Quality_Issues-Client-${payload.clientId}.csv`);
    yield put(actionCreators.ExportDataQualityIssuesSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetDataQualityIssuesError(e.response));
  }
}

function* onGetDataQualityIssues({
  payload,
}: Action<{ clientId: number, limit: number, page: number }>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `api/v1/data-admin/client/${payload.clientId}/data-quality/issues`,
      {
        params: {
          limit: payload.limit || 100,
          page: payload.page || 0,
        },
      },
    );
    yield put(actionCreators.GetDataQualityIssuesSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetDataQualityIssuesError(e.response));
  }
}

export function* onExportDataFile({
  payload,
}: Action<ExportDataFileRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.departmentId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `api/v1/data-admin/client/${payload.clientId}/organisational-unit/${payload.departmentId}/sip/system/file`,
      {
        responseType: "blob",
      },
    );

    downloadXslsFile(
      response.data,
      `Client ${payload.clientId}, Department ${payload.departmentId} SIP File export.xlsx`,
    );
    yield put(actionCreators.ExportDataFileSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.ExportDataFileError(e.response));
  }
}

export function* onDownloadTransferHistoryFile({
  payload,
}: Action<DownloadTransferHistoryFileRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.documentId || !payload.name) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `api/v1/data-admin/client/${payload.clientId}/sip/data-transfer/document/${payload.documentId}`,
      {
        responseType: "blob",
      },
    );

    downloadXslsFile(response.data, payload.name);
    yield put(actionCreators.DownloadTransferHistoryFileSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.DownloadTransferHistoryFileError(e.response));
  }
}

export function* onDExportSipFileExceptions({
  payload,
}: Action<{
  clientId: number,
  fileId: number,
  exceptionTypeId: number,
  exceptionTypeName: string,
}>): Generator<*, void, *> {
  try {
    if (
      !payload ||
      !payload.clientId ||
      !payload.fileId ||
      !payload.exceptionTypeId ||
      !payload.exceptionTypeName
    ) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `api/v1/data-admin/client/${payload.clientId}/sip/files/${payload.fileId}/exception-types/${payload.exceptionTypeId}/exceptions/file`,
      {
        responseType: "blob",
      },
    );

    downloadXslsFile(
      response.data,
      `Client ${payload.clientId} SIP Validation ${payload.exceptionTypeName}.xlsx`,
    );
    yield put(actionCreators.ExportSipFileExceptionsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.ExportSipFileExceptionsError(e.response));
  }
}

function* onReviewDataChanges({ payload }: Action<UpdateDataFile>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.file) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const file = { ...payload.file, status: DATA_FILE_STATUSES.GENERATING_REVIEW };
    const response = yield call(
      http.post,
      `/api/v1/data-admin/client/${payload.clientId}/sip/files/${payload.file.id}/generate-review`,
      file,
    );

    yield put(actionCreators.SetDataFileStatus("GENERATING_REVIEW"));
  } catch (e) {
    yield put(actionCreators.ApproveDataExceptionsError(e.response));
  }
}

type GetReviewData = $Exact<{
  clientId: number,
  fileId: number,
}>;

function* onGetReviewDataChanges({ payload }: Action<GetReviewData>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.fileId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v1/data-admin/client/${payload.clientId}/sip/files/${payload.fileId}/review`,
    );

    yield put(actionCreators.GetReviewDataChangesSuccess(response.data.categories));
  } catch (e) {
    yield put(actionCreators.ApproveDataExceptionsError(e.response));
  }
}

export default function* dataSaga(): Generator<*, void, void> {
  yield all([
    takeEvery(GetDataFiles, onGetDataFiles),
    takeEvery(GetValidatingStatus, onGetValidatingStatus),
    takeEvery(CreateDataFile, onCreateDataFile),
    takeEvery(GetActiveHierarchy, onGetActiveHierarchy),
    takeEvery(GetExportHistory, onGetExportHistory),
    takeEvery(ExportFileLogHistory, onExportFileLogHistory),
    takeEvery(CancelProcessing, onCancelProcessing),
    takeEvery(GetTransferHistory, onGetTransferHistory),
    takeEvery(PostDataTransferFile, onPostDataTransferFile),
    takeEvery(ExportTransferFileHistory, onExportTransferFileHistory),
    takeEvery(GetDataExceptionTypes, onGetDataExceptionTypes),
    takeEvery(GetDataExceptionsByType, onGetDataExceptionsByType),
    takeEvery(SetDataExceptionTypeConfirm, onSetDataExceptionTypeConfirm),
    takeEvery(DeleteDataFile, onDeleteDataFile),
    takeEvery(ApproveDataExceptions, onApproveDataExceptions),
    takeEvery(GetDataDashboardOverview, onGetDataDashboardOverview),
    takeEvery(GetDataQualityIssues, onGetDataQualityIssues),
    takeEvery(ExportDataQualityIssues, onExportDataQualityIssues),
    takeEvery(ExportDataFile, onExportDataFile),
    takeEvery(DownloadTransferHistoryFile, onDownloadTransferHistoryFile),
    takeEvery(ExportSipFileExceptions, onDExportSipFileExceptions),
    takeEvery(DeleteTransferHistoryFile, onDeleteTransferHistoryFile),
    takeEvery(ReviewDataChanges, onReviewDataChanges),
    takeEvery(GetReviewDataChanges, onGetReviewDataChanges),
  ]);
  yield spawn(dataFileArchive);
}
